"use client";

import React, { useRef } from "react";
import { Search, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, ChartBar as BarChart3, FileText, Sparkles, CircleAlert as AlertCircle } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
const auditHighlights = [{
  title: "Personal Technical Analysis",
  description: "I personally investigate and interpret every audit finding—evaluating your site architecture, indexing health, and search visibility without automated template exports."
}, {
  title: "Prioritised Action Plan",
  description: "I categorize every technical issue by commercial impact and implementation effort, so your team knows exactly what to fix first."
}];
export default function Heroaudit() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const isReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isReduced) return;
    const tl = gsap.timeline({
      defaults: {
        ease: "power3.out",
        duration: 0.8
      }
    });
    tl.from(".hero-badge", {
      opacity: 0,
      y: -16
    }).from(".hero-heading", {
      opacity: 0,
      y: 20
    }, "-=0.5").from(".hero-subtext", {
      opacity: 0,
      y: 20
    }, "-=0.6").from(".hero-actions", {
      opacity: 0,
      y: 20
    }, "-=0.6").from(".hero-metrics", {
      opacity: 0,
      y: 20
    }, "-=0.5").from(".hero-card", {
      opacity: 0,
      scale: 0.95,
      duration: 0.9
    }, "-=0.7");
  }, {
    scope: containerRef
  });
  return <section id="heroaudittransparency" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28 text-foreground">
      {/* Background Glows */}
      <div className="pointer-events-none absolute -top-24 -right-24 size-96 rounded-full bg-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/4 size-80 rounded-full bg-accent/10 blur-3xl" />

      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Copy & CTAs */}
          <div className="lg:col-span-7">
            <div className="hero-badge mb-6 inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1.5 text-xs font-medium text-foreground bg-secondary">
                <Sparkles className="size-3.5 text-primary" />
                <span>Transparent &amp; Independent SEO Audits</span>
              </Badge>
            </div>

            <h1 className="hero-heading font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              SEO Audit
            </h1>

            <p className="hero-subtext mt-6 text-base text-muted-foreground sm:text-lg lg:text-xl max-w-2xl leading-relaxed">
              I rigorously examine your technical crawlability, on-page architecture, and organic search footprint to uncover the exact barriers restricting your revenue. A focused audit gives your leadership and development teams the diagnostic clarity needed to unlock commercial growth.
            </p>

            {/* Action Buttons */}
            <div className="hero-actions mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button size="lg" className="group rounded-md px-6 text-base font-semibold" asChild>
                <Link to="/ContactPage">
                  <span>Book a Free Consultation</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" className="rounded-md px-6 text-base font-semibold" asChild>
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </div>

            {/* Value Highlights */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-border pt-8">
              <div className="flex items-center gap-2.5">
                <ShieldCheck className="size-5 shrink-0 text-primary" />
                <span className="text-xs font-medium text-foreground">Plain-English Reporting</span>
              </div>
              <div className="flex items-center gap-2.5">
                <BarChart3 className="size-5 shrink-0 text-primary" />
                <span className="text-xs font-medium text-foreground">Actionable ROI Focus</span>
              </div>
            </div>

            {/* Key Metrics / Consolidated Statement Strip */}
            <div className="hero-metrics mt-10 rounded-2xl border border-border bg-card p-6 shadow-sm">
              <p className="text-sm sm:text-base font-medium text-foreground leading-relaxed">
                I personally evaluate, test, and interpret every audit finding myself—ensuring all strategic recommendations are technically accurate, commercially grounded, and directly actionable for your developers.
              </p>
            </div>
          </div>

          {/* Right Column: Consultant Profile & Deliverable Card */}
          <div className="lg:col-span-5">
            <div className="hero-card relative rounded-2xl border border-border bg-card p-6 md:p-8 shadow-xl">
              
              {/* Consultant Header */}
              <div className="flex items-center gap-4 mb-6">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Independent SEO Consultant" loading="lazy" decoding="async" className="size-16 rounded-full object-cover border-2 border-primary/20 shrink-0" />
                <div>
                  <h2 className="text-base font-bold text-card-foreground font-default">
                    Yassine Essaadi
                  </h2>
                  <p className="text-xs text-muted-foreground font-medium">
                    Freelance SEO Strategy &amp; Technical Audit Specialist
                  </p>
                  <div className="mt-1 inline-flex items-center gap-1.5 text-[12.5px] text-primary font-semibold">
                    <span className="size-2 rounded-full bg-primary animate-pulse" />
                    Available for New SEO Consultations
                  </div>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Scope Label */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="size-4 text-primary" />
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    What My Audit Delivers
                  </span>
                </div>
                <Badge variant="outline" className="text-[11.5px] uppercase font-mono">
                  Clear Roadmap
                </Badge>
              </div>

              {/* Highlights List */}
              <div className="space-y-4">
                {auditHighlights.map((item, index) => <div key={index} data-index={index} className="group rounded-xl border border-border/80 bg-background/50 p-4 transition-all hover:border-primary/40 hover:bg-background">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                      <div>
                        <h3 className="text-sm font-semibold text-foreground font-default">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>

              {/* Disclaimer */}
              <div className="mt-6 flex items-center gap-3 rounded-xl bg-muted p-3 text-xs text-muted-foreground">
                <AlertCircle className="size-4 shrink-0 text-primary" />
                <span>
                  Every recommendation is tailored strictly to your business model, targets, and developer capacity.
                </span>
              </div>

              {/* Bottom CTA */}
              <div className="mt-6">
                <Button className="w-full justify-center rounded-md font-semibold" size="default" asChild>
                  <Link to="/ServicesPage">
                    <Search className="size-4" />
                    <span>View All SEO Services</span>
                  </Link>
                </Button>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}